import { AppLayout, Flashbar, FlashbarProps, Link, MixedLineBarChartProps, SplitPanel, Tabs } from '@amzn/awsui-components-core';
import React, { useState } from 'react';

import { buildBreadcrumbChain } from 'src/utilities/RouteConfig';
import { DFPHomePageSideNavigation } from '../../dfp-home-page/DFPHomePageSideNavigation';
import { ChartSummaryContext } from '../../generic-components/ai-summary-components/ChartSummaryContext';
import { ChartSummaryPanel } from '../../generic-components/ai-summary-components/ChartSummaryPanel';
import { SasContext } from './SasContext';
import { SasDetailsTab } from './SasDetailsTab';
import { SaSDetailsTabTableData, SaSSummaryContainerInfo, StatusType } from './SasInterfaces';
import { SasOverViewContent } from './SasOverview';
import { SasSelectionFilters } from './SasSelectionFilters';

export const SasPageMain: React.FC = () => {
  const [flashbarItems, setFlashbarItems] = useState<FlashbarProps.MessageDefinition[]>([]);

  // Chart Summary State
  const [summaries, setSummaries] = useState<Array<{ title: string; content: string; summaryId: string; timestamp: number }>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSplitPanelOpen, setIsSplitPanelOpen] = useState<boolean>(false);

  const addSummary = (title: string, content: string, summaryId = '') => {
    setSummaries((prev) => [{ title, content, summaryId, timestamp: Date.now() }, ...prev]);
  };

  const clearSummaries = () => {
    setSummaries([]);
  };

  const [multiselectStatus, setMultiselectStatus] = useState<StatusType>('loading');
  const [overViewStatus, setOverViewStatus] = useState<StatusType>('loading');
  const [detailsStatus, setDetailsStatus] = useState<StatusType>('loading');

  //Graphs and Domains
  const [riskResponseGraphData, setRiskResponseGraphData] = useState<MixedLineBarChartProps.BarDataSeries<string | number | Date>[]>([]);
  const [riskResponseGraphDataXDomain, setRiskResponseGraphDataXDomain] = useState<string[]>([]);
  const [riskResponseGraphDataYDomain, setRiskResponseGraphDataYDomain] = useState<number[]>([]);
  const [totalOpenRiskGraphData, setTotalOpenRiskGraphData] = useState<MixedLineBarChartProps.BarDataSeries<string | number | Date>[]>([]);
  const [totalOpenRiskGraphDataXDomain, setTotalOpenRiskGraphDataXDomain] = useState<string[]>([]);
  const [totalOpenRiskGraphDataYDomain, setTotalOpenRiskGraphDataYDomain] = useState<number[]>([]);
  const [campaignsGraphData, setCampaignsGraphData] = useState<MixedLineBarChartProps.BarDataSeries<string | number | Date>[]>([]);
  const [campaignsGraphDataXDomain, setCampaignsGraphDataXDomain] = useState<string[]>([]);
  const [campaignsGraphDataYDomain, setCampaignsGraphDataYDomain] = useState<number[]>([]);
  const [totalOpenRecommendationsGraphData, setTotalOpenRecommendationsGraphData] = useState<
    MixedLineBarChartProps.BarDataSeries<string | number | Date>[]
  >([]);
  const [totalOpenRecommendationsGraphDataXDomain, setTotalOpenRecommendationsGraphDataXDomain] = useState<string[]>([]);
  const [totalOpenRecommendationsGraphDataYDomain, setTotalOpenRecommendationsGraphDataYDomain] = useState<number[]>([]);
  const [averageRiskDowGraphData, setAverageRiskDowGraphData] = useState<MixedLineBarChartProps.BarDataSeries<string | number | Date>[]>([]);
  const [averageRiskDowGraphDataXDomain, setAverageRiskDowGraphDataXDomain] = useState<string[]>([]);
  const [averageRiskDowGraphDataYDomain, setAverageRiskDowGraphDataYDomain] = useState<number[]>([]);

  // Details Tab
  const [sasSummaryContainer, setSasSummaryContainer] = useState<SaSSummaryContainerInfo>({} as SaSSummaryContainerInfo);

  //Tables
  const [sasDetailsTableData, setSasDetailsTableData] = useState<SaSDetailsTabTableData>({} as SaSDetailsTabTableData);
  const [sasTotalRiskResourceTableData, setSasTotalRiskResourceTableData] = useState<SaSDetailsTabTableData>({} as SaSDetailsTabTableData);
  const [sasManagerMomTableData, setSasManagerMomTableData] = useState<SaSDetailsTabTableData>({} as SaSDetailsTabTableData);
  const [sasOpenTotalRiskTableData, setSasOpenTotalRiskTableData] = useState<SaSDetailsTabTableData>({} as SaSDetailsTabTableData);

  const [summaryFilters, setSummaryFilters] = useState<string>('');
  const [originalFilterOptions, setOriginalFilterOptions] = useState<string>('');

  const displayNotification = (status: FlashbarProps.Type, message: string | React.ReactNode, linkMessage?: string) => {
    function addTicketLink() {
      if (linkMessage) {
        return (
          <Link variant="primary" target="_blank" href={linkMessage} color="inverted" fontSize="body-m">
            {' '}
            submit a ticket
          </Link>
        );
      }
    }
    setFlashbarItems([
      {
        type: status,
        content: (
          <>
            {message} 'or ' {addTicketLink()}
          </>
        ),
        dismissible: true,
        dismissLabel: 'Dismiss message',
        onDismiss: () => setFlashbarItems([]),
        id: 'message_1'
      }
    ]);
  };

  return (
    <ChartSummaryContext.Provider
      value={{
        summaries,
        addSummary,
        isLoading,
        setIsLoading,
        isSplitPanelOpen,
        setIsSplitPanelOpen,
        clearSummaries
      }}
    >
      <AppLayout
        contentType="cards"
        headerSelector="#h"
        navigation={<DFPHomePageSideNavigation />}
        toolsHide={true}
        notifications={<Flashbar items={flashbarItems} />}
        splitPanel={
          <SplitPanel
            header="Chart Summary"
            i18nStrings={{
              preferencesTitle: 'Split panel preferences',
              preferencesPositionLabel: 'Position',
              preferencesPositionDescription: 'Choose the default split panel position.',
              preferencesPositionSide: 'Side',
              preferencesPositionBottom: 'Bottom',
              preferencesConfirm: 'Confirm',
              preferencesCancel: 'Cancel',
              closeButtonAriaLabel: 'Close panel',
              openButtonAriaLabel: 'Open panel',
              resizeHandleAriaLabel: 'Resize split panel'
            }}
            closeBehavior="hide"
          >
            <ChartSummaryPanel />
          </SplitPanel>
        }
        onSplitPanelToggle={({ detail }) => setIsSplitPanelOpen(detail.open)}
        splitPanelPreferences={{ position: 'side' }}
        splitPanelOpen={isSplitPanelOpen}
        splitPanelSize={400}
        maxContentWidth={Number.MAX_VALUE}
        content={
          <div>
            <SasContext.Provider
              value={{
                displayNotification,
                multiselectStatus,
                setMultiselectStatus,

                overViewStatus,
                setOverViewStatus,

                detailsStatus,
                setDetailsStatus,

                sasSummaryContainer,
                setSasSummaryContainer,

                riskResponseGraphData,
                setRiskResponseGraphData,
                riskResponseGraphDataXDomain,
                setRiskResponseGraphDataXDomain,
                riskResponseGraphDataYDomain,
                setRiskResponseGraphDataYDomain,

                totalOpenRiskGraphData,
                setTotalOpenRiskGraphData,
                totalOpenRiskGraphDataXDomain,
                setTotalOpenRiskGraphDataXDomain,
                totalOpenRiskGraphDataYDomain,
                setTotalOpenRiskGraphDataYDomain,

                campaignsGraphData,
                setCampaignsGraphData,
                campaignsGraphDataXDomain,
                setCampaignsGraphDataXDomain,
                campaignsGraphDataYDomain,
                setCampaignsGraphDataYDomain,

                totalOpenRecommendationsGraphData,
                setTotalOpenRecommendationsGraphData,
                totalOpenRecommendationsGraphDataXDomain,
                setTotalOpenRecommendationsGraphDataXDomain,
                totalOpenRecommendationsGraphDataYDomain,
                setTotalOpenRecommendationsGraphDataYDomain,

                averageRiskDowGraphData,
                setAverageRiskDowGraphData,
                averageRiskDowGraphDataXDomain,
                setAverageRiskDowGraphDataXDomain,
                averageRiskDowGraphDataYDomain,
                setAverageRiskDowGraphDataYDomain,

                sasDetailsTableData,
                setSasDetailsTableData,
                sasTotalRiskResourceTableData,
                setSasTotalRiskResourceTableData,
                sasManagerMomTableData,
                setSasManagerMomTableData,
                sasOpenTotalRiskTableData,
                setSasOpenTotalRiskTableData,

                summaryFilters,
                setSummaryFilters,
                originalFilterOptions,
                setOriginalFilterOptions
              }}
            >
              <SasSelectionFilters />
              <Tabs
                tabs={[
                  {
                    label: 'Overview',
                    id: 'sas-OV',
                    content: <SasOverViewContent />
                  },
                  {
                    label: 'Details',
                    id: 'sas-DT',
                    content: <SasDetailsTab />
                  }
                ]}
              />
            </SasContext.Provider>
          </div>
        }
      />
    </ChartSummaryContext.Provider>
  );
};
